import { useRef } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { CircleAlert, ImagePlus, Loader2, Sparkles, Trash2 } from "lucide-react"
import { type GlassesDigitizationSummary } from "types"

export const GlassesDigitizationCard = ({
  canStartDigitization,
  digitization,
  feedbackMessage,
  isStarting,
  needsEffect,
  onAddSourceImages,
  onRemoveSourceImage,
  onStartDigitization,
  onVisibleChange,
  showInStoreDisabled,
  startRestrictionMessage,
  sourceImages,
  visible,
}: {
  canStartDigitization: boolean
  digitization: GlassesDigitizationSummary
  feedbackMessage?: string | null
  isStarting: boolean
  needsEffect?: boolean
  onAddSourceImages: (files: File[]) => void
  onRemoveSourceImage: (imageId: string) => void
  onStartDigitization: () => void
  onVisibleChange: (visible: boolean) => void
  showInStoreDisabled: boolean
  startRestrictionMessage?: string | null
  sourceImages: Array<{ id: string; previewUrl: string }>
  visible: boolean
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const isProcessing =
    isStarting || digitization.status === "queued" || digitization.status === "processing"
  const toggleDisabled = showInStoreDisabled || isProcessing

  return (
    <Card
      className={cn("relative overflow-hidden", needsEffect && "border-destructive")}
      data-testid="glasses-digitization-card"
    >
      {needsEffect ? (
        <div
          className="border-b border-destructive/20 bg-destructive/5 px-6 py-3"
          data-testid="glasses-digitization-validation"
        >
          <p className="text-sm text-destructive">Generate a try-on effect before saving.</p>
        </div>
      ) : null}
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1">
          <CardTitle>AR Model Creator</CardTitle>
          <CardDescription>
            Add some photos from different angles to start the creation process.
          </CardDescription>
        </div>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-sm text-muted-foreground">Try on Enabled</span>
                <Switch
                  data-testid="glasses-try-on-toggle"
                  checked={visible}
                  disabled={toggleDisabled}
                  onCheckedChange={onVisibleChange}
                  className="data-[state=checked]:bg-vto-accent"
                />
              </div>
            </TooltipTrigger>
            {toggleDisabled ? (
              <TooltipContent>
                {isProcessing
                  ? "Available once the AR model is ready."
                  : "Finish the AR model and required product info to enable Try-On."}
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-3">
          {sourceImages.map((image) => (
            <div
              key={image.id}
              data-testid="glasses-source-image"
              data-source-image-id={image.id}
              className="group relative size-28 overflow-hidden rounded-xl border bg-muted"
            >
              <img src={image.previewUrl} alt="" className="h-full w-full object-cover" />
              <button
                disabled={isProcessing}
                data-testid="glasses-source-image-remove"
                className="absolute right-2 top-2 rounded-full bg-background/90 p-1.5 opacity-0 shadow transition group-hover:opacity-100"
                type="button"
                onClick={() => onRemoveSourceImage(image.id)}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {sourceImages.length < 3 ? (
            <>
              <input
                ref={fileInputRef}
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                disabled={isProcessing}
                multiple
                type="file"
                data-testid="glasses-source-images-input"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? [])
                  if (files.length > 0) onAddSourceImages(files)
                  event.target.value = ""
                }}
              />
              <button
                type="button"
                disabled={isProcessing}
                data-testid="glasses-source-images-add"
                className={cn(
                  "flex size-28 items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 text-muted-foreground transition hover:border-vto-accent/40 hover:bg-muted/40 hover:text-foreground",
                  isProcessing && "cursor-not-allowed opacity-50"
                )}
                onClick={() => fileInputRef.current?.click()}
              >
                <ImagePlus className="h-6 w-6" />
              </button>
            </>
          ) : null}
        </div>

        <div className="space-y-3">
          {digitization.status === "ready" ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full bg-transparent"
                onClick={onStartDigitization}
                disabled={!canStartDigitization || isStarting}
                data-testid="glasses-recreate-model"
              >
                {isStarting ? <Loader2 className="animate-spin" /> : null}
                Recreate AR Model
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                If you're not satisfied with the result, try uploading another photos.
              </p>
            </>
          ) : (
            <Button
              type="button"
              size="lg"
              className="w-full"
              onClick={onStartDigitization}
              disabled={!canStartDigitization || isStarting}
              data-testid="glasses-create-model"
            >
              {isStarting ? <Loader2 className="animate-spin" /> : <Sparkles />}
              {digitization.status === "failed" ? "Recreate AR Model" : "Create AR Model"}
            </Button>
          )}
          {feedbackMessage && feedbackMessage !== startRestrictionMessage ? (
            <div
              className="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              data-testid="glasses-digitization-feedback"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{feedbackMessage}</p>
            </div>
          ) : null}
          {startRestrictionMessage ? (
            <div
              className="flex items-start gap-3 rounded-xl border border-amber-200/80 bg-amber-50/90 px-4 py-3 text-sm text-amber-900"
              data-testid="glasses-digitization-restriction"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p>{startRestrictionMessage}</p>
            </div>
          ) : null}
        </div>
      </CardContent>
      {isProcessing ? (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-5 rounded-[inherit] bg-background/70 backdrop-blur-sm"
          data-testid="glasses-digitization-progress"
        >
          <p className="text-sm font-medium text-foreground">Creating try-on</p>
          <div className="w-full max-w-[200px]">
            <Progress indeterminate className="h-1" aria-label="Digitization in progress" />
          </div>
          <p className="text-xs text-muted-foreground">Usually takes 1-3 min</p>
        </div>
      ) : null}
    </Card>
  )
}
